import { FSNode } from '../../../types/fs-types';
import { BasicPlatformApi, DirectoryData } from '../../../types';
import LVGLSchema from './LVGLSchema';
import { XMLParseCallback } from './common';
import { clearGlobalScreenDefinitionsForFile, clearKnownProjectValidationState } from './XMLValidatorCore';
import { collectPropertyParamSchemas, resolveScreenIdentifier } from './validationHelpers';
import { isAbsoluteFolderPath, joinFsPath, normalizeFilePathForComparison, normalizePath } from '../../../utils/path';
import { parsePropertyName } from '../../../utils/propertyName';
import { DEFAULT_LVGL_SCHEMA_VERSION } from '../../../services/constants';
import { getXmlParseErrorElement } from '../../../services/xmlParseError';
import { ResourceManager } from './ResourceManager';
import {
    ParsedTarget,
    ParsedTargetMemoryRegion,
    parseTargetMemoryRegions as parseProjectTargets
} from './AssetMemoryAnalyzer';

// Types and interfaces ending with `Schema` are for the `SchemaService`.
// Those ending with `Data` are for code generation.
//
// What's different? 'Schema' defines the data structures which contain
// the schema, the expected data "shape" of the LVGL XMLs. 'Data' describes
// what the valid XMLs look like when parsed. These are similar but not
// the same.
//
// 'Data' types ought to be tightly-coupled with the `Schema` types.
// Wherever possible, they are directly derived.

// Indicates what aspect of a schema is being defined
export type ElementSchemaType =
    | 'lvgl-element'
    | 'lvgl-element-child'
    | 'lvgl-widget' // LVGL widgets, built into LVGL
    | 'widget' // Custom "user" widgets, defined in an XML project
    | 'component' // Custom "user" components, defined in an XML project
    | 'screen'
    | 'element-child'
    | 'element';

/** Registry entry for a screen XML file (keyed by normalized file path). */
export type ScreenNameEntry = {
    filePath: string;
    /** Explicit `name` attribute from `<screen>`, if set. */
    nameAttribute?: string;
    /** True when the `<screen>` root has `permanent="true"`. */
    permanent?: boolean;
};

type SubjectNamesByType = Record<string, Set<string>>;

// 'Element' is either Component or Widget, user-defined or LVGL built-in
// TODO: It may be better to have separate types for <element> and
// all widgets and components
// [Architectural Improvement]
export type ElementSchema = {
    name: string;
    filePath?: string;
    type: ElementSchemaType;
    allowedChildren: string[] | 'any';
    parent?: string; // Possible parent, which is optional, used for extra lookup of items that have the same name
    extends: string;
    styles?: StyleSchema[];
    properties?: PropertySchema[];
    elements?: string[];
    access?: string;
    args?: ArgSchema[];
    help?: string;
    /** Timeline names defined in the element's `<animations>` section. */
    timelines?: string[];
    /**
     * Required relative order of this element's direct children, as groups of interchangeable
     * element names. Children belonging to an earlier group must appear before children
     * belonging to a later group (siblings within the same group may appear in any order
     * relative to each other). Children not listed in any group are unordered. An element name
     * may be listed in more than one consecutive group to let it float freely relative to those
     * groups' other members, while still respecting the order of groups before and after them.
     */
    order?: string[][];
};

// Not sure it provides any benefit to "derive" the *Data
// interface by removing almost all properties from the base
// *Schema inteface, and then re-defining them.
export type ElementData = Omit<
    ElementSchema,
    'filePath' | 'allowedChildren' | 'extends' | 'elements' | 'help' | 'styles'
> & {
    type: 'component' | 'widget';
    // No properties on 'ElementData', lookup corresponding `ElementSchema` instead
    styles: StyleBlocks[];
    consts: Consts;
    animations: AnimDefinitionData[];
    enums: string[];
    views: ElementViewData[];
    slots: string[]; // Array of slot names, if any / applicable
};

export type ConstDefinitionData = {
    type: string;
    name: string;
    value: string;
    help?: string;
    filePath?: string;
};

export type ConstVariants = {
    def: ConstDefinitionData;
    targets: string[];
}[];

// Corresponds to one or <const>...</const> blocks, merged together by name
export type Consts = {
    [name: string]: ConstVariants;
};

export type Animation = {
    prop: string;
    target: string;
    // These are strings because they could be const refs
    start: string;
    end: string;
    duration: string;
    delay: string;
    early_apply?: boolean;
};

export type IncludeTimeline = {
    target: string;
    timeline: string;
    delay: string;
};

export type AnimDefinitionData = {
    name: string;
    animations: Animation[];
    include_timelines: IncludeTimeline[];
    repeat_count?: number | 'infinite';
    repeat_delay?: number; // non-negative integer
};

export type ViewChildData = {
    type: string;
    children?: ViewChildData[];
    props: { [key: string]: any }; // For other properties like width, height, src etc.
};

export type ElementViewData = {
    extends: string;
    children: ViewChildData[];
    styles: string[];
    targets: string[];
    props: { [key: string]: any }; // For other properties
};

// TODO: PropertyParamScheme and ArgSchema are intentionally subsets of PropertySchema, since
// they are often used interchangeably. However, sometimes it really matters (like knowing if
// if something is a param or not, hence `isParam`). I think it would be cleaner to make this
// a compound type with a determinant field.
// [Architectural Improvement]
export type PropertySchema = {
    name: string;
    type: string;
    help?: string | null;
    default?: string;
    selector?: boolean; // Supports -hyphenated-selector-syntax (https://docs.lvgl.io/master/xml/ui_elements/styles.html#local-styles)
    required?: boolean; // Not used by LVGL schemas, but used by the built-in schema
    params?: PropertyParamSchema[];
    values?: string[];
};

export type PropertyParamSchema = {
    name: string;
    type: string;
    help?: string;
    default?: string;
    isParam: boolean;
};

export type ArgSchema = {
    name: string;
    type: string;
    help?: string;
    default?: string;
};

export type EnumSchema = {
    help?: string;
    values: EnumValueSchema[];
    filePath?: string;
};

export type EnumValueSchema = {
    name: string;
    value?: string;
    help?: string;
};

export type StyleSchema = {
    name: string;
    value: string;
    help?: string;
    filePath?: string;
};

export type StyleData = {
    name: string;
    help?: string;
    properties: Record<string, string>;
};

export type StyleBlocks = {
    targets: string[];
    styles: StyleData[];
};

export type ColorDefinitionSchema = {
    name?: string;
    value: string;
    filePath: string;
};

export type RegisteredElement = {
    elementName: string;
    element: Element;
    filePath: string;
    elementType: ElementSchemaType;
    elementExtends?: string;
    access?: string;
};

export type TranslationLookup = { tag: string; files: string[] };

// NOTE: Most of these are project-level files; 'test' is just an element
export const NON_RELOADABLE_XML_ELEMENTS: string[] = ['globals', 'project', 'test', 'translations'];
export const OPTIONAL_PROJECT_XML_ELEMENTS: string[] = ['translations', 'test'];

// Only these top-level project folders are scanned (recursively) for XML files.
// Anything else at the project root (fonts, images, lib, preview-bin, etc.) is left alone.
export const XML_FOLDERS: ReadonlySet<string> = new Set(['screens', 'widgets', 'components']);

// If null, returns the fallback value
//
// this is because '' || undefined will evaluate to undefined,
// but in many cases we want it to be undefined only if the left-hand-side
// is null, but the empty string if it's not null.
const ifNull = <T>(v: T | null, fallback: any): T | any => (v === null ? fallback : v);

export class LVGLSchemaService {
    private static instance: LVGLSchemaService | null = null;
    private platformApi: BasicPlatformApi;
    private static projectPath: string;
    private parseXml: XMLParseCallback;
    private readonly hotReloadListeners: Set<(filePath: string) => void> = new Set<(filePath: string) => void>();

    public static LVGL_SCHEMA_VERSIONS: string[] = ['v9.4.0', 'v9.5.0'];

    public static LVGLVersion: string = DEFAULT_LVGL_SCHEMA_VERSION;

    // NOTE: for all these maps, we should use a common .set method to check
    // if the key already exists, because silently overwriting an existing
    // item is a good way to hide errors.
    // NOTE: elements will have to become private to force the use of the
    // provided methods to access the schema data. This is because the
    // lookup is more complicated than just a simple key search, now.
    // (Partly due to lv_obj- elements, which may be used in shorthand.)
    public elements: Map<string, ElementSchema> = new Map();

    public globalStyleProps: Map<string, PropertySchema> = new Map(); // All allowable style props from the LVGL schema
    public globalStyles: Map<string, StyleSchema> = new Map(); // All the styles in globals.xml
    public globalApiProps: Map<string, PropertySchema> = new Map(); // All the props in the <api> of globals.xml
    public globalConsts: Map<string, ConstDefinitionData> = new Map();
    public consts: Map<string, ConstDefinitionData> = new Map();

    public enums: Map<string, EnumSchema> = new Map();
    public colors: ColorDefinitionSchema[] = [];
    public fonts: string[] = [];
    public images: string[] = []; // Just a list of all image names, from all image blocks
    public projectTargets: ParsedTarget[] = [];
    public projectFolders: string[] = [];
    public subjectsByType: SubjectNamesByType = {};
    // Root and library globals.xml files are parsed independently; keep per-file state so one cannot wipe another.
    private readonly subjectsByFile: Map<string, SubjectNamesByType> = new Map<string, SubjectNamesByType>();
    public translationTagsByFile: Map<string, string[]> = new Map();
    public screenNames: Map<string, ScreenNameEntry> = new Map();

    public getScreenIdentifier(entry: ScreenNameEntry): string {
        return resolveScreenIdentifier(entry.filePath, entry.nameAttribute);
    }

    /**
     * Returns the known screen identifiers. Pass `{ permanent: true }` to get only
     * permanent screens (loadable via screen_load_event) or `{ permanent: false }`
     * for non-permanent screens (created via screen_create_event).
     */
    public getScreenNames(filter?: { permanent?: boolean }): string[] {
        const entries: ScreenNameEntry[] = [...this.screenNames.values()].filter((entry) =>
            filter?.permanent === undefined ? true : Boolean(entry.permanent) === filter.permanent
        );
        return Array.from(new Set(entries.map((entry) => this.getScreenIdentifier(entry)))).sort((a, b) =>
            a.localeCompare(b)
        );
    }

    /** Returns another screen file path that already uses this identifier, if any. */
    public findScreenNameConflict(identifier: string, excludeFilePath: string): string | undefined {
        if (!identifier) return undefined;

        const normalizedExclude: string = normalizePath(excludeFilePath);
        for (const [path, entry] of this.screenNames) {
            if (path === normalizedExclude) continue;
            if (this.getScreenIdentifier(entry) === identifier) return path;
        }

        return undefined;
    }

    /** Register or update a screen from its file path, optional `name`, and `permanent` flag (on save or while editing). */
    public registerScreenName(filePath: string, nameAttribute?: string | null, permanent?: boolean): void {
        const normalizedPath: string = normalizePath(filePath);
        const trimmedName: string | undefined = nameAttribute?.trim() || undefined;

        clearGlobalScreenDefinitionsForFile(normalizedPath);

        const entry: ScreenNameEntry = { filePath: normalizedPath };
        if (trimmedName) entry.nameAttribute = trimmedName;
        if (permanent) entry.permanent = true;

        this.screenNames.set(normalizedPath, entry);
    }

    public unregisterScreenName(filePath: string): void {
        const normalizedPath: string = normalizePath(filePath);

        this.screenNames.delete(normalizedPath);
        clearGlobalScreenDefinitionsForFile(normalizedPath);
    }

    public get subjects(): string[] {
        return Object.values(this.subjectsByType).reduce(
            (accum: string[], subject) => accum.concat(Array.from(subject)),
            []
        );
    }

    public getTranslationTags(): string[] {
        const allTags: Set<string> = new Set<string>();
        this.translationTagsByFile.forEach((tags: string[]) => {
            tags.forEach((tag: string) => {
                if (tag) allTags.add(tag);
            });
        });
        return Array.from(allTags).sort((a, b) => a.localeCompare(b));
    }

    public getProjectTargetNames(): string[] {
        return this.projectTargets.map((target: ParsedTarget) => target.name).sort((a, b) => a.localeCompare(b));
    }

    /**
     * Returns memory names that are valid for globals asset block autocomplete.
     * With no target selection it returns the union of memory names from all known project targets.
     * With one or more targets it returns only the intersection, so a compound
     * target like "small|large" only suggests memory regions available in every selected target.
     */
    public getProjectMemoryNames(targetNames: string[] = []): string[] {
        if (targetNames.length === 0) return this.getAllMemoryNamesUnion();

        const selectedTargets: ParsedTarget[] = targetNames
            .map((targetName: string) => this.projectTargets.find((target: ParsedTarget) => target.name === targetName))
            .filter((target: ParsedTarget | undefined): target is ParsedTarget => Boolean(target));

        if (selectedTargets.length === 0) return [];

        const [firstTarget, ...remainingTargets] = selectedTargets;
        const memoryNames: Set<string> = new Set(
            firstTarget.memories.map((memory: ParsedTargetMemoryRegion) => memory.name)
        );

        for (const target of remainingTargets) {
            const targetMemoryNames: Set<string> = new Set(
                target.memories.map((memory: ParsedTargetMemoryRegion) => memory.name)
            );

            for (const memoryName of Array.from(memoryNames)) {
                if (!targetMemoryNames.has(memoryName)) memoryNames.delete(memoryName);
            }
        }

        return Array.from(memoryNames).sort((a, b) => a.localeCompare(b));
    }

    public getDuplicateTranslationTags(): TranslationLookup[] {
        const occurrences: Map<string, Set<string>> = new Map<string, Set<string>>();

        this.translationTagsByFile.forEach((tags: string[], filePath: string) => {
            tags.forEach((tag: string) => {
                if (!tag) return;
                if (!occurrences.has(tag)) occurrences.set(tag, new Set<string>());
                occurrences.get(tag)!.add(filePath);
            });
        });

        return Array.from(occurrences.entries())
            .filter(([, files]) => files.size > 1)
            .map(([tag, files]) => ({
                tag,
                files: Array.from(files).sort((a, b) => a.localeCompare(b))
            }))
            .sort((a, b) => a.tag.localeCompare(b.tag));
    }

    // It is important that this service does not take 'platformApi' directly or use DOMParser directly.
    // Those are both browser-only and this service must also run in Node.
    constructor(platformApi: BasicPlatformApi, projectPath: string, parseXml: XMLParseCallback) {
        LVGLSchema(this); // Define LVGL schemas

        this.platformApi = platformApi;
        LVGLSchemaService.projectPath = projectPath;
        this.parseXml = parseXml;
    }

    public onHotReload(cb: (filePath: string) => void): () => void {
        this.hotReloadListeners.add(cb);
        return (): void => {
            this.hotReloadListeners.delete(cb);
        };
    }

    private getAllMemoryNamesUnion(): string[] {
        const allMemoryNames: Set<string> = new Set();

        for (const target of this.projectTargets) {
            for (const memory of target.memories) {
                allMemoryNames.add(memory.name);
            }
        }

        return Array.from(allMemoryNames).sort((a, b) => a.localeCompare(b));
    }

    static getInstance(
        platformApi?: BasicPlatformApi,
        projectPath?: string,
        parseXml?: XMLParseCallback
    ): LVGLSchemaService | undefined {
        if (!this.instance && platformApi && parseXml) {
            // NOTE: projectPath may be an empty string, that's okay
            this.instance = new LVGLSchemaService(platformApi, projectPath || '', parseXml);
            return this.instance;
        } else if (this.instance) return this.instance;
        return undefined;
    }

    /**
     * Normalize and validate a raw `lvgl_version` attribute value (e.g. "9.5.0") from
     * project.xml into a supported schema version (e.g. "v9.5.0"), falling back to
     * DEFAULT_LVGL_SCHEMA_VERSION when it is missing or unsupported.
     */
    public static normalizeLvglVersion(rawVersion: string | null | undefined): string {
        if (rawVersion) {
            const versioned: string = 'v' + rawVersion;
            if (LVGLSchemaService.LVGL_SCHEMA_VERSIONS.includes(versioned)) return versioned;
        }
        return DEFAULT_LVGL_SCHEMA_VERSION;
    }

    private async parseProjectFile(): Promise<void> {
        const projectXmlPath: string = joinFsPath(LVGLSchemaService.projectPath, 'project.xml');
        const projectXmlContent: string = await this.platformApi.getFileData(projectXmlPath);
        const document: Document = this.parseXml(projectXmlContent);

        // Project targets
        this.parseProjectTargets(document);
        // Project folders
        this.parseProjectFolders(document);

        // `lvgl_version`
        const rawVersion: string | null | undefined = document.querySelector('project')?.getAttribute('lvgl_version');
        LVGLSchemaService.LVGLVersion = LVGLSchemaService.normalizeLvglVersion(rawVersion);

        if (rawVersion && LVGLSchemaService.LVGL_SCHEMA_VERSIONS.includes('v' + rawVersion)) {
            console.log(`Using "lvgl_version" ${LVGLSchemaService.LVGLVersion}, as specified in project.xml`);
        } else {
            console.debug(
                `The "lvgl_version" was missing/unsupported in project.xml, defaulting to ${DEFAULT_LVGL_SCHEMA_VERSION}`
            );
            LVGLSchemaService.LVGLVersion = DEFAULT_LVGL_SCHEMA_VERSION;
        }
    }

    // This exists to avoid importing multiple versions of the same schema class in different modules.
    public getLVGLVersion(): string {
        return LVGLSchemaService.LVGLVersion;
    }

    // Load LVGL global schema
    async loadGlobalSchema(): Promise<void> {
        // Must specially parse this from the project project.xml file first... so we know what version
        // of the global schemas to load.
        await this.parseProjectFile();

        // Get resources path based on dev/prod mode
        const resourcesPath: string = await this.platformApi.getResourcesSourcePath();
        if (!resourcesPath) throw new Error('Could not determine resources source path');

        const globalsPath = joinFsPath(
            ResourceManager.getXmlSchemasPath(resourcesPath, LVGLSchemaService.LVGLVersion),
            'globals.xml'
        );

        const content: string = await this.platformApi.getFileData(globalsPath);
        const document: Document = this.parseXml(content);

        // Parse global enums
        document.querySelectorAll('enumdef').forEach((element: Element) => this.processEnums(globalsPath, element));

        // Parse global styles
        const styleElementProps: PropertySchema[] = [];
        document.querySelectorAll('styledef prop').forEach((prop: Element) => {
            const name: string | null = prop.getAttribute('name');
            const type: string | null = prop.getAttribute('type');
            const help: string | null = prop.getAttribute('help');
            const _default = ifNull(prop.getAttribute('default'), undefined);
            if (!name || !type) return;
            this.globalStyleProps.set(name, {
                name,
                type,
                help,
                default: _default
            });
            styleElementProps.push({ name, type, help, default: _default });
        });

        // Add style element properties to global style element
        const styleElement: ElementSchema | undefined = this.getElement('style');
        if (styleElement) {
            styleElement.properties = [...(styleElement.properties || []), ...styleElementProps];
        } else {
            throw new Error(`Missing global style element`);
        }
    }

    // Load all LVGL widget schemas from resources
    async loadDefaultElementSchemas(): Promise<void> {
        const resourcesPath: string = await this.platformApi.getResourcesSourcePath();

        if (!resourcesPath) throw new Error('Could not determine resources source path');

        const lvglVersion = LVGLSchemaService.LVGLVersion;

        const xmlsDirFiles: DirectoryData | null = await this.platformApi.getDirectoryData(
            ResourceManager.getXmlSchemasPath(resourcesPath, lvglVersion)
        );

        // load all LVGL widget schemas from resources starting with "lv_" (excluding globals.xml)
        const elements = xmlsDirFiles?.files.filter((file: FSNode) => file.name.startsWith('lv_')) || [];

        if (!elements.length) {
            throw new Error('XML Schemas could not be found');
        }

        // Load all LVGL elements in parallel
        await Promise.all(elements.map((element) => this.loadElementSchema(element.path, 'lvgl-widget')));

        // Add style_ properties to preview element from 'lv_obj'
        const lv_objStyleProps: PropertySchema[] | undefined = this.elements
            .get('lv_obj')
            ?.properties?.filter((p) => p.name.startsWith('style_'))
            .map((p) => {
                let pp = p;
                pp.selector = p.name.endsWith('-selector');
                pp.name = p.name.replace('-selector', ''); // Have to remove '-selector' from the name so these properties work properly.
                return pp;
            });

        const previewElement: ElementSchema | undefined = this.elements.get('preview');
        if (previewElement && lv_objStyleProps) {
            previewElement.properties = [
                ...(previewElement.properties || []),
                ...lv_objStyleProps.map((p) => ({
                    ...p,
                    required: false
                }))
            ];
        }
    }

    // The sibling project folders declared via <folders> in project.xml - i.e. `projectFolders`
    // (already root-inclusive, see `parseProjectFolders`) minus the root project itself. Use this
    // whenever you only want the *additional* included projects (VFS loading, build/codegen
    // tooling) rather than the root-inclusive list schema loading uses, so the "what counts as an
    // included project" filter lives in one place.
    getIncludedProjectFolders(projectPath: string): string[] {
        const projectPathComparisonKey: string = normalizeFilePathForComparison(normalizePath(projectPath));
        return this.projectFolders.filter(
            (folder) => normalizeFilePathForComparison(folder) !== projectPathComparisonKey
        );
    }

    // Loads the root project's schemas, plus the schemas of every project referenced by
    // project.xml's <folders> (e.g. component libraries living outside the project directory),
    // in the order they're declared.
    async loadAllProjectSchemas(): Promise<(ElementSchema | null)[]> {
        this.screenNames.clear();

        const projectFolders: string[] = this.projectFolders;

        // Loaded sequentially (not in parallel) so that precedence follows `projectFolders` order deterministically
        // rather than whichever folder's file I/O happens to finish last.
        const results: (ElementSchema | null)[][] = [];
        for (const folder of projectFolders) {
            results.push(await this.loadProjectSchemas(folder));
        }

        return results.flat();
    }

    // Loads .xmls from the specified project directory (does not resolve included project folders)
    async loadProjectSchemas(projectPath: string): Promise<(ElementSchema | null)[]> {
        const dirFiles = await this.platformApi.getDirectoryData(projectPath);

        // Recursively collect all XML files from a directory (unrestricted below the top level)
        const collectXmlFiles = (files: FSNode[]): FSNode[] => {
            let xmlFiles: FSNode[] = [];

            for (const file of files) {
                if (file.type === 'directory' && file.children)
                    xmlFiles = [...xmlFiles, ...collectXmlFiles(file.children)];
                else if (file.type === 'file' && file.name.endsWith('.xml')) xmlFiles.push(file);
            }

            return xmlFiles;
        };

        const topLevelFiles: FSNode[] = dirFiles?.files ?? [];

        // Directories are only descended into if they're on the approved list
        const allXmlFiles: FSNode[] = topLevelFiles.reduce<FSNode[]>((xmlFiles, file) => {
            if (file.type === 'file' && file.name.endsWith('.xml')) {
                xmlFiles.push(file);
            } else if (file.type === 'directory' && file.children && XML_FOLDERS.has(file.name)) {
                xmlFiles.push(...collectXmlFiles(file.children));
            }

            return xmlFiles;
        }, []);

        // load everything in parallel
        return Promise.all(allXmlFiles.map((file) => this.loadElementSchema(file.path)));
    }

    // Loads all the LVGL XML schemas and .xml files for the given project
    async loadEverythingInParallel(projectPath: string): Promise<(ElementSchema | null)[]> {
        LVGLSchemaService.projectPath = projectPath;
        this.clearAllDataStructures();
        LVGLSchema(this); // Define LVGL schemas

        // This actually has to happen first
        await this.loadGlobalSchema();
        // LVGL widget/element schemas must load before project components so `<children allow="events">` resolves.
        await this.loadDefaultElementSchemas();
        return this.loadAllProjectSchemas();
    }

    async hotReloadProject(projectPath: string, triggerPath: string = projectPath): Promise<void> {
        await this.loadEverythingInParallel(projectPath);
        this.emitHotReload(triggerPath);
    }

    private emitHotReload(filePath: string): void {
        this.hotReloadListeners.forEach((listener: (filePath: string) => void): void => {
            listener(filePath);
        });
    }

    // Parse 'globals.xml'
    async parseGlobals(document: Document, filePath: string): Promise<void> {
        this.clearConstsForFile(filePath);
        // Register enums
        document.querySelectorAll('api > enumdef').forEach((element: Element) => this.processEnums(filePath, element));

        // Register global API props
        document.querySelectorAll('api > prop').forEach((prop: Element) => {
            const name: string | null = prop.getAttribute('name');
            const type: string | null = prop.getAttribute('type');
            const params: PropertyParamSchema[] = collectPropertyParamSchemas(prop, {
                requireName: true,
                requireType: true
            });

            if (name && ((type?.trim().length ?? 0) > 0 || params.length > 0)) {
                this.globalApiProps.set(name, {
                    name,
                    type: type?.trim() ?? '',
                    help: prop.getAttribute('help') || undefined,
                    params
                });
            }
        });

        // Register consts
        this.registerConstsFromDocument(document, filePath, true);

        // Register styles
        document.querySelectorAll('styles > style').forEach((style: Element) => {
            const name: string | null = style.getAttribute('name');
            if (name) {
                this.globalStyles.set(name, {
                    name,
                    value: '', // Not needed in this case
                    help: style.getAttribute('help') ?? undefined,
                    filePath
                });
            }
        });

        // Register fonts (concat across files, since multiple globals.xml can be parsed per project)
        const fonts: string[] = [...this.fonts];
        document.querySelectorAll('fonts > *').forEach((font: Element) => {
            const name: string | null = font.getAttribute('name');
            if (name && !fonts.includes(name)) fonts.push(name);
        });
        this.fonts = fonts;

        // Register images (concat across files, since multiple globals.xml can be parsed per project)
        const images: string[] = [...this.images];
        document.querySelectorAll('images > *').forEach((image: Element) => {
            const name: string | null = image.getAttribute('name');
            if (name && !images.includes(name)) images.push(name);
        });
        this.images = images;

        this.registerSubjectsFromDocument(document, filePath);

        // Register colors (concat across files, since multiple globals.xml can be parsed per project)
        const colors: ColorDefinitionSchema[] = [...this.colors];
        document.querySelectorAll('colors > color').forEach((color: Element) => {
            const name: string | null = color.getAttribute('name');
            if (name && !colors.some((c) => c.value === color.getAttribute('value'))) {
                colors.push({
                    name,
                    value: color.getAttribute('value') || '',
                    filePath
                });
            }
        });
        this.colors = colors;
    }

    private parseProjectTargets(document: Document): void {
        this.projectTargets = parseProjectTargets(document).targets;
    }

    // Parses the folders declared in project.xml's <folders>, resolving each to an absolute
    // path relative to the project root and de-duping by a comparison-normalized key (not raw
    // string equality - two paths that point at the same directory, e.g. differing only in a
    // trailing slash, slash direction, or drive-letter casing, must be treated as the same
    // folder), keeping the first occurrence. Order is preserved, since it's the precedence order
    // used for globals files.
    // If the project root itself isn't among them (no "." entry, or no <folders> at all), it's
    // prepended so the root project is always loaded first (highest precedence).
    private parseProjectFolders(document: Document): void {
        const declaredFolders: string[] = Array.from(document.querySelectorAll('folders > folder'))
            .map((folderNode) => folderNode.getAttribute('path'))
            .filter((path): path is string => Boolean(path));

        const projectPath: string = LVGLSchemaService.projectPath;
        const seenComparisonKeys: Set<string> = new Set<string>();
        const normalizedFolders: string[] = [];
        for (const folder of declaredFolders) {
            const resolvedFolder: string = normalizePath(
                isAbsoluteFolderPath(folder) ? folder : joinFsPath(projectPath, folder)
            );
            const comparisonKey: string = normalizeFilePathForComparison(resolvedFolder);
            if (seenComparisonKeys.has(comparisonKey)) continue;
            seenComparisonKeys.add(comparisonKey);
            normalizedFolders.push(resolvedFolder);
        }

        const normalizedRootProjectPath: string = normalizePath(projectPath);
        const rootProjectComparisonKey: string = normalizeFilePathForComparison(normalizedRootProjectPath);
        this.projectFolders = seenComparisonKeys.has(rootProjectComparisonKey)
            ? normalizedFolders
            : [normalizedRootProjectPath, ...normalizedFolders];
    }

    private registerSubjectsFromDocument(document: Document, filePath: string): void {
        const subjectsByType: SubjectNamesByType = {};

        document.querySelectorAll('subjects > *').forEach((subject: Element) => {
            const name: string | null = subject.getAttribute('name');
            const type: string = subject.tagName.toLowerCase();
            if (name) {
                if (!subjectsByType[type]) subjectsByType[type] = new Set<string>();
                subjectsByType[type].add(name);
            }
        });

        this.subjectsByFile.set(normalizePath(filePath), subjectsByType);
        this.rebuildSubjectsByType();
    }

    private rebuildSubjectsByType(): void {
        const nextSubjectsByType: SubjectNamesByType = {};

        this.subjectsByFile.forEach((subjectsByType: SubjectNamesByType) => {
            Object.entries(subjectsByType).forEach(([type, subjectNames]: [string, Set<string>]) => {
                if (!nextSubjectsByType[type]) nextSubjectsByType[type] = new Set<string>();
                subjectNames.forEach((name: string) => nextSubjectsByType[type].add(name));
            });
        });

        this.subjectsByType = nextSubjectsByType;
    }

    private isRootProjectXml(filePath: string): boolean {
        return normalizePath(filePath) === normalizePath(joinFsPath(LVGLSchemaService.projectPath, 'project.xml'));
    }

    // Load an element schema from a given path
    async loadElementSchema(
        filePath: string,
        elementType?: ElementSchemaType,
        shouldReload: boolean = true
    ): Promise<ElementSchema | null> {
        this.clearConstsForFile(filePath);
        // Get the element name from the path
        const elementName: string = filePath.replace(/^.*[\\/]/, '').replace('.xml', '');
        if (!elementName) return null;

        // If the schema is already loaded, return it
        if (this.elements.has(elementName)) {
            const schema: ElementSchema | undefined = this.getElement(elementName);
            // If the schema is an 'lvgl-element', return it, LVGL elements are hardcoded and cannot be replaced
            if (schema && elementType?.startsWith('lvgl-')) {
                return schema;
            } else if (schema && !shouldReload) {
                // If the cached schema should not be reloaded, return it
                return schema;
            } else if (!NON_RELOADABLE_XML_ELEMENTS.includes(elementName)) {
                // Delete the cached schema, if it is reloadable, to reload it
                this.elements.delete(elementName);
            }
        }

        // Load the element schema from the file
        const content: string = await this.platformApi.getFileData(filePath);
        if (!content) return null;

        const document: Document = this.parseXml(content);
        const parseError: Element | null = getXmlParseErrorElement(document);

        if (parseError) {
            throw new Error(`Error parsing XML file: ${filePath}. Error details: ${parseError.textContent}`);
        }

        if (elementName === 'project' || document.documentElement.tagName === 'project') {
            if (!this.isRootProjectXml(filePath)) return null;
            this.parseProjectTargets(document);
            return null;
        }

        // If the element is 'globals.xml', parse it
        if (elementName === 'globals') {
            await this.parseGlobals(document, filePath);
            return null;
        }

        if (document.documentElement.tagName === 'translations') {
            const languageSchema: ElementSchema | undefined = this.elements.get('translation');

            if (languageSchema) {
                let languages: string[] = [];
                const languagesAttr = document.documentElement.getAttribute('languages');
                if (languagesAttr) {
                    languages = languagesAttr.split(' ');
                }

                // add detected languages to the schema as allowed properties
                languages.forEach((language: string) => {
                    if (!languageSchema.properties?.find((p) => p.name === language)) {
                        languageSchema.properties?.push({
                            name: language,
                            type: 'string:translation',
                            default: 'NULL'
                        });
                    }
                });

                this.elements.set('translation', languageSchema);
            }

            const translationTags: string[] = [];
            document.querySelectorAll('translation').forEach((translationElement: Element) => {
                const tag: string | null = translationElement.getAttribute('tag');
                if (tag && tag.trim().length > 0) translationTags.push(tag);
            });
            if (translationTags.length > 0) this.translationTagsByFile.set(filePath, translationTags);
            else this.translationTagsByFile.delete(filePath);

            return languageSchema === undefined ? null : languageSchema;
        }

        // If the element type is not provided, try to infer it from the file
        // only component and widget are supported as root elements
        if (!elementType) {
            // Get the root element of the document
            const rootElement: HTMLElement = document.documentElement;

            if (rootElement?.tagName === 'component') elementType = 'component';
            else if (rootElement?.tagName === 'widget') elementType = 'widget';
            else if (rootElement?.tagName === 'screen') elementType = 'screen';
            else if (['project', 'test'].includes(rootElement?.tagName)) return null;
            else
                throw new Error(
                    `Could not determine element type for "${elementName}" ("${rootElement?.tagName}" ${filePath}).`
                );
        }

        if (elementType === 'screen') {
            this.registerScreenName(
                filePath,
                document.documentElement.getAttribute('name'),
                document.documentElement.getAttribute('permanent') === 'true'
            );
        }

        this.registerConstsFromDocument(document, filePath, false);

        // Parse local colors
        document.querySelectorAll('consts > color').forEach((color: Element) => {
            const name: string | null = color.getAttribute('name');
            const value: string | null = color.getAttribute('value');
            if (!value) return;
            if (!this.colors.some((c) => c.value === value) && name) {
                this.colors.push({
                    name,
                    value,
                    filePath
                });
            }
        });

        // Create a new element schema
        const schema: ElementSchema = {
            name: elementName,
            filePath: filePath,
            type: elementType || 'element',
            extends: elementName === 'lv_obj' ? '' : 'lv_obj',
            properties: [],
            elements: [],
            styles: [],
            allowedChildren: 'any'
        };

        // Parse element enums and add to global enums
        document.querySelectorAll('api > enumdef').forEach((enumDef: Element) => this.processEnums(filePath, enumDef));

        // Parse element styles
        document.querySelectorAll('styles > style').forEach((style: Element) => {
            const name: string | null = style.getAttribute('name');
            if (!name) return;

            schema.styles?.push({
                name,
                value: style.getAttribute('value') || ''
            });
        });

        schema.timelines = [
            ...new Set(
                Array.from(document.querySelectorAll('animations > timeline'))
                    .map((timeline: Element) => timeline.getAttribute('name'))
                    .filter((name: string | null): name is string => Boolean(name))
            )
        ];

        // Parse view and add extends
        if (document.querySelector('view') && document.querySelector('view')?.hasAttribute('extends')) {
            const viewExtends: string | null | undefined = document.querySelector('view')?.getAttribute('extends');
            if (viewExtends) schema.extends = viewExtends;
        }

        // Parse element properties
        document.querySelectorAll('api > prop').forEach((prop: Element) => {
            const name: string | null = prop.getAttribute('name');
            if (!name) throw new Error(`Property without a 'name' in '${schema.name}'`);

            const params: PropertyParamSchema[] = collectPropertyParamSchemas(prop);

            schema.properties?.push({
                name,
                type: prop?.getAttribute('type') ?? '',
                help: prop.getAttribute('help'),
                default: ifNull(prop.getAttribute('default'), undefined),
                params
            });
        });

        document.querySelectorAll('api > element').forEach((element: Element) => {
            if (!element.getAttribute('name')) return;
            const name: string = `${elementName}-${element.getAttribute('name')}`;
            schema.elements?.push(name);

            this.registerAsElement({
                elementName: name,
                element,
                filePath,
                elementType: elementName.startsWith('lv_') ? 'lvgl-element-child' : 'element-child',
                elementExtends: element?.getAttribute('type') ?? '',
                access: element?.getAttribute('access') ?? ''
            });
        });

        document.querySelectorAll('api > slot').forEach((slot: Element) => {
            if (!slot.getAttribute('name')) return;
            const name: string = `${elementName}-${slot.getAttribute('name')}`;
            schema.elements?.push(name);

            this.registerAsElement({
                elementName: name,
                element: slot,
                filePath,
                elementType: elementName.startsWith('lv_') ? 'lvgl-element-child' : 'element-child',
                elementExtends: 'lv_obj'
            });
        });

        const childrenAllowAttr: string | null =
            document.querySelector('api > children')?.getAttribute('allow') ?? null;
        if (childrenAllowAttr?.trim()) {
            const allowTokens: string[] = childrenAllowAttr.trim().split(/\s+/).filter(Boolean);
            schema.allowedChildren = this.resolveApiChildrenAllow(elementName, allowTokens);
        }

        // Set the new schema
        this.elements.set(elementName, schema);
        return schema;
    }

    registerAsElement({ elementName, element, filePath, elementType, elementExtends, access }: RegisteredElement) {
        // TODO: Not sure if this is the best way to populate the optional parent element
        // this is mainly to differentiate between lv_obj- elements (in shorthand) and other a few
        // other elements
        // [Architectural Improvement]
        const parentType: 'lv_obj' | undefined = elementName.startsWith('lv_obj-') ? 'lv_obj' : undefined;

        const schema: ElementSchema = {
            name: elementName,
            filePath: filePath,
            // Fall back to 'lv_obj' for add/get access, otherwise use what is specified.
            // It may be empty, which is fine. Some kinds of elements, like custom access, do not extend anything.
            extends: ['add', 'get'].includes(access || '') ? elementExtends || 'lv_obj' : elementExtends || '',
            type: elementType,
            properties: [],
            elements: [],
            allowedChildren: [],
            parent: parentType,
            access,
            // Parse args
            args: Array.from(element.querySelectorAll('arg') || []).map((arg: Element): ArgSchema => ({
                name: arg.getAttribute('name') || '',
                type: arg.getAttribute('type') || '',
                default: ifNull(arg.getAttribute('default'), undefined),
                help: ifNull(arg.getAttribute('help'), undefined)
            }))
        };

        if (elementExtends && elementExtends != '') {
            // Set this to 'any' so it will be looked up when needed.
            // If we tried to compute it now, it'd be incomplete because
            // we cannot guarantee all LVGL XML schemas have been loaded
            // before this one.
            schema.allowedChildren = 'any';
        }

        // Parse properties
        element.querySelectorAll('prop').forEach((prop: Element) => {
            const name: string | null = prop.getAttribute('name');
            if (!name) return;

            const params: PropertyParamSchema[] = collectPropertyParamSchemas(prop, {
                requireName: true,
                useEmptyStringForMissingHelp: true
            });

            schema.properties?.push({
                name,
                help: prop.getAttribute('help') || '',
                type: prop.getAttribute('type') || '',
                // It is important this be undefined if null, an empty string is
                // considered a valid default. Undefined means there is no default.
                default: ifNull(prop.getAttribute('default'), undefined),
                params
            });
        });

        // Register the element
        this.elements.set(elementName, schema);
    }

    private processEnums(filePath: string, enumDef: Element): void {
        const name: string | null = enumDef.getAttribute('name');
        if (!name) throw new Error(`Enum definition without name found: ${enumDef}`);

        const values: EnumValueSchema[] = Array.from(enumDef.querySelectorAll('enum')).map(
            (en: Element) =>
                ({
                    name: en.getAttribute('name'),
                    value: en.getAttribute('value'),
                    help: en.getAttribute('help')
                }) as EnumValueSchema
        );
        this.enums.set(name, {
            values,
            filePath,
            help: enumDef.getAttribute('help') || ''
        });
    }

    // Delete an element schema from the elements collection
    deleteElementSchema(filePath?: string): void {
        if (!filePath) return;
        const elements: [string, ElementSchema][] = Array.from(this.elements.entries()).filter(
            ([_, schema]) => schema.filePath === filePath
        );
        if (elements?.length) elements.forEach(([name]) => this.elements.delete(name));

        this.clearConstsForFile(filePath);

        // Delete all colors related to the filePath
        this.colors = this.colors.filter((c) => c.filePath !== filePath);

        // Delete all enums related to the filePath
        this.enums.forEach((enumDef: EnumSchema, name: string) => {
            if (enumDef.filePath === filePath) this.enums.delete(name);
        });

        // Delete all global styles related to the filePath
        this.globalStyles.forEach((style: StyleSchema, name: string) => {
            if (style.filePath === filePath) this.globalStyles.delete(name);
        });

        this.translationTagsByFile.delete(filePath);

        if (this.screenNames.has(normalizePath(filePath))) this.unregisterScreenName(filePath);
    }

    getEnum(enumType: string): EnumSchema | undefined {
        // Make sure there is no trailing '[]' in the enum type
        enumType = enumType.endsWith('[]') ? enumType.slice(0, -2) : enumType;
        return this.enums.get(enumType);
    }

    // Get all enum values for a given enum type
    // Used in HoverProvider to show enum values in tooltip
    getEnumValues(enumType: string): string[] {
        const enumDef: EnumSchema | undefined = this.getEnum(enumType);
        if (enumDef) return enumDef.values.map((v) => v.name);

        return [];
    }

    // Get a given property or arg.
    // This may be used to lookup the type of a prop param by passing
    // prop-param format to propNameFull.
    getProperty(
        elementName: string,
        propNameFull: string,
        extendsElement?: string,
        parentElement?: string
    ): [PropertySchema | PropertyParamSchema | undefined, string | undefined] | undefined {
        const schema: ElementSchema | undefined = this.getElement(elementName);
        if (!schema) return undefined;

        const { propName, paramName } = parsePropertyName(propNameFull);

        // Special case: style properties
        if (parentElement === 'styles' && elementName === 'style') {
            const prop: PropertySchema | undefined = this.globalStyleProps.get(propNameFull);
            if (prop && prop.type) return [prop, elementName];
        }

        const lookupProperty = (
            schemaToCheck: ElementSchema,
            ownerElementName: string
        ): [PropertySchema | PropertyParamSchema | undefined, string | undefined] => {
            const prop: PropertySchema | undefined = schemaToCheck.properties?.find((p) => p.name === propName);
            if (prop?.type) return [prop, ownerElementName];

            if (paramName) {
                if (!prop) return [undefined, undefined];

                const param: PropertyParamSchema | undefined = prop.params?.find((p) => p.name === paramName);
                return [param ? { ...param, isParam: true } : undefined, ownerElementName];
            }

            if (prop) {
                // Could not find the prop type, and there is no param name,
                // try to find a param with the same name as the prop.
                // This is the shorthand.
                const paramWithPropName: PropertyParamSchema | undefined = prop.params?.find(
                    (param) => param.name === propName
                );
                if (paramWithPropName) {
                    return [{ ...paramWithPropName, isParam: true }, ownerElementName];
                }
            } else {
                const arg: ArgSchema | undefined = schemaToCheck.args?.find((a) => a.name === propName);
                if (arg?.type) return [arg, ownerElementName];
            }

            return [undefined, undefined];
        };

        if (extendsElement) {
            let foundProp: PropertySchema | undefined;
            let foundElement: string | undefined;

            // Wrapper elements like <component> and <widget> expose the referenced
            // definition's API surface via `extends`. Those API props must shadow
            // wrapper props with the same name, such as the wrapper's built-in `name`.
            this.walkInheritanceChain(
                extendsElement,
                (inheritedSchema: ElementSchema, inheritedElementName: string) => {
                    const [prop, owner] = lookupProperty(inheritedSchema, inheritedElementName);
                    if (!prop || !owner) return false;

                    foundProp = prop;
                    foundElement = owner;
                    return true;
                }
            );

            if (foundProp) return [foundProp, foundElement];
        }

        const [ownProp, ownOwner] = lookupProperty(schema, elementName);
        if (ownProp) return [ownProp, ownOwner];

        let foundProp: PropertySchema | undefined;
        let foundElement: string | undefined;

        this.walkInheritanceChain(schema.extends, (inheritedSchema: ElementSchema, inheritedElementName: string) => {
            const [type, owner] = lookupProperty(inheritedSchema, inheritedElementName);
            if (!type || !owner) return false;

            foundProp = type;
            foundElement = owner;
            return true;
        });

        return [foundProp, foundElement];
    }

    // Get the type of a given property or arg.
    // This may be used to lookup the type of a prop param by passing
    // prop-param format to propNameFull.
    getPropertyType(
        elementName: string,
        propNameFull: string,
        extendsElement?: string,
        parentElement?: string
    ): [string | undefined, string | undefined] {
        const result = this.getProperty(elementName, propNameFull, extendsElement, parentElement);
        if (result !== undefined) {
            return [result[0]?.type, result[1]];
        }

        return [undefined, undefined];
    }

    // Get the values of a given property
    // Used in 'CompletionProvider' to show property values in suggestion.
    getPropertyValues(elementName: string, propName: string): string[] | PropertyParamSchema[] {
        const schema: ElementSchema | undefined = this.getElement(elementName);
        if (!schema) return [];

        const prop: PropertySchema | undefined = schema?.properties?.find((p) => p.name === propName);
        if (prop?.values) return prop.values;
        if (prop?.params) return prop.params;
        return [];
    }

    // Get all available properties for a given element.
    // Used in 'CompletionProvider' to show properties in suggestion; this also returns element args.
    getPropertyNames(elementName: string, extendedElement?: string, parentType?: string): [string[], string[]] {
        const schema: ElementSchema | undefined = this.getElement(elementName, parentType);

        if (schema) {
            const expandPropertyNames = (props: PropertySchema[] | undefined): string[] =>
                props
                    ?.map((prop) => [
                        prop.name,
                        // Include prop params with the notation: prop-param
                        ...(prop.params || []).map((param) =>
                            /* Detect shorthand for a param with the same name as the property
                            e.g., the valid option for a property named 'bind_text' with a param
                            'bind_text' is the shorthand 'bind_text'. 'bind_text-bind_text' is
                            not allowed. */
                            prop.name === param.name ? prop.name : prop.name + '-' + param.name
                        )
                    ])
                    .flat() || [];

            const elementProperties: string[] = expandPropertyNames(schema.properties).concat(
                schema.args?.map((a) => a.name) || []
            );

            let inheritedProperties: string[] = [];
            let isDirectInheritedElement: boolean = true;
            this.walkInheritanceChain(extendedElement || schema.extends, (inheritedSchema: ElementSchema) => {
                if (!isDirectInheritedElement && inheritedSchema.type === 'component') return;

                if (inheritedSchema.properties) {
                    const inheritedFromSchema: string[] = expandPropertyNames(inheritedSchema.properties)
                        .concat(inheritedSchema.args?.map((a) => a.name) || [])
                        .sort();
                    inheritedProperties = [...inheritedProperties, ...inheritedFromSchema];
                }

                // After the first call in the chain, flip it, so only direct component props are added.
                isDirectInheritedElement = false;
            });

            const inheritedPropertySet: Set<string> = new Set(inheritedProperties);
            // If a referenced component/widget API prop collides with a wrapper prop,
            // keep only the referenced API version in the effective property set.
            const effectiveElementProperties: string[] = extendedElement
                ? elementProperties.filter((propName) => !inheritedPropertySet.has(propName))
                : elementProperties;

            return [[...effectiveElementProperties.sort()], [...inheritedProperties]];
        }

        return [[], []];
    }

    // Get the help text for a given property
    // Used in HoverProvider to show property help in tooltip
    // Used in CompletionProvider to show property help in suggestion
    getPropertyHelp(elementName: string, propName: string): string | undefined {
        return this.getElement(elementName)?.properties?.find((p) => p.name === propName)?.help || undefined;
    }

    // Get the help text for a given element
    // Used in CompletionProvider to show element help in suggestion
    getElementHelp(elementName: string): string | undefined {
        return this.getElement(elementName)?.help;
    }

    // Get the help text for a given enum value
    // Used in CompletionProvider to show enum value help in suggestion
    getEnumHelp(enumType: string, value: string): string | undefined {
        const enumDef: EnumSchema | undefined = this.enums.get(enumType);
        if (enumDef) {
            const enumValue: EnumValueSchema | undefined = enumDef.values.find((v) => v.name === value);
            return enumValue?.help;
        }
        return undefined;
    }

    // Get all properties for a given element
    // Used in CompletionProvider to show style properties in suggestion
    // Used in HoverProvider to show style properties in tooltip
    getElementProperties(elementName: string, appendInheritedProperties: boolean = true): PropertySchema[] {
        const schema: ElementSchema | undefined = this.getElement(elementName);
        const elementProperties: PropertySchema[] = Array.from(schema?.properties?.values() || []);

        // Get all properties from the element's extended schemas
        let inheritedProperties: PropertySchema[] = [];

        // Get properties from all extended schemas
        if (schema?.extends && appendInheritedProperties) {
            this.walkInheritanceChain(schema.extends, (inheritedSchema: ElementSchema) => {
                if (inheritedSchema.properties)
                    inheritedProperties = [...inheritedProperties, ...inheritedSchema.properties.values()];
            });
        }

        // Combine and sort all properties alphabetically
        return [...elementProperties.sort(), ...inheritedProperties.sort()];
    }

    hasElementProperties(elementName: string): boolean {
        const schema: ElementSchema | undefined = this.getElement(elementName);
        if (!schema) return false;
        if (schema?.properties?.length || elementName === 'style') return true;

        if (schema.extends) {
            let hasProperties: boolean = false;

            this.walkInheritanceChain(schema.extends, (inheritedSchema: ElementSchema) => {
                if (!inheritedSchema.properties?.length) return false;
                hasProperties = true;
                return true;
            });

            return hasProperties;
        }
        return false;
    }

    // Returns true if a child element tag represents an attachable event
    // (e.g. event_cb, screen_load_event, subject_set_int_event). Events are
    // `access="add"` children of lv_obj whose tag is `event_cb` or ends with `_event`.
    isEventElement(tagName: string): boolean {
        const tag: string = tagName.replace(/^lv_obj-/, '');
        if (tag !== 'event_cb' && !tag.endsWith('_event')) return false;
        const schema: ElementSchema | undefined = this.getElement(tag);
        return schema?.access === 'add';
    }

    // Returns the XML tag names of all event elements that can be attached to a
    // widget, derived from the lv_obj schema (all widgets extend lv_obj).
    getEventElementNames(): string[] {
        const objSchema: ElementSchema | undefined = this.elements.get('lv_obj');
        if (!objSchema?.elements?.length) return [];

        return objSchema.elements
            .map((fullName: string): string => fullName.replace(/^lv_obj-/, ''))
            .filter((tag: string): boolean => this.isEventElement(tag));
    }

    // Returns the editable fields (args + props) for an event/child element,
    // normalized into PropertySchema. Args without a default are marked required.
    getEventElementFields(tagName: string): PropertySchema[] {
        const schema: ElementSchema | undefined = this.getElement(tagName);
        if (!schema) return [];

        const argFields: PropertySchema[] = (schema.args ?? []).map((arg: ArgSchema): PropertySchema => ({
            name: arg.name,
            type: arg.type,
            help: arg.help,
            default: arg.default,
            required: arg.default === undefined
        }));

        const propFields: PropertySchema[] = (schema.properties ?? []).map((prop: PropertySchema): PropertySchema => ({
            ...prop
        }));

        return [...argFields, ...propFields];
    }

    // Checks if the given name is an element of the given definition
    isElementOf(name: string, def: ElementSchema): boolean {
        // Prefix, if necessary
        // Elements in the schema should have the prefix of their parent type
        if (!name.startsWith(def.name)) name = `${def.name}-${name}`; // for example: 'style' -> 'lv_obj-style'

        return Boolean(def?.elements?.includes(name));
    }

    getUltimateAncestor(type: string, depth = 0): string {
        // Intentionally not using 'this.getElement'
        const schema: ElementSchema | undefined = this.elements.get(type);

        // If the element has an extends, recursively get the ultimate ancestor
        if (schema?.extends) return this.getUltimateAncestor(schema.extends, depth + 1);

        return type;
    }

    // Get allowed children for a given XML element name
    // Used in CompletionProvider to show allowed children in suggestion
    // Used in XMLValidator to validate allowed children
    //
    // NOTE: Do not use this to determine what children and element may have
    // IN THE PROCESS of loading schemas. It will be incomplete.
    //
    // NOTE: To handle the optional shorthand of lv_obj- elements, this returns
    // those elements with and without the 'lv_obj-' prefix. If used for auto-completion,
    // that'll cause duplicates.
    //
    // TODO: It could be a good idea to cache the computed allowed children.
    // Redo every time elements are loaded or changed.
    // [Architectural Improvement]
    //
    getAllowedChildrenForElement(elementName: string): string[] {
        const schema: ElementSchema | undefined = this.getElement(elementName);
        if (!schema) {
            console.warn(`Cannot find allowed children for '${elementName}'`);
            return [];
        }

        if (schema.allowedChildren === 'any') {
            if (elementName === 'lv_obj') {
                // lv_obj is the base widget; build the full allowed child list.

                // Start with only lv_obj- elements (and their shorthand)
                // (shorthand comes first)
                return (
                    Array.from(this.elements.entries())
                        // lv_obj- elements, in shorthand
                        // example: lv_obj-bind_style will be bind_style
                        .map(([name, s]): [string, ElementSchema] | null =>
                            name.startsWith('lv_obj-') ? [name.replace('lv_obj-', ''), s] : null
                        )
                        // lv_obj- elements, long form
                        // example: lv_obj-bind_style will be lv_obj-bind_style
                        .concat(Array.from(this.elements.entries()).filter(([name, _]) => name.startsWith('lv_obj-')))
                        .filter((entry) => entry !== null)

                        // lv_obj- elements, long form
                        // example: lv_obj-bind_style will be lv_obj-bind_style

                        .concat(
                            Array.from(this.elements.entries()).filter(
                                ([_, s]) =>
                                    // lv_obj- elements (like bind, event_cb, etc.)
                                    s.name.startsWith('lv_obj') ||
                                    // OR... it's not a view and it's one of the list
                                    (s.name !== 'view' &&
                                        ['lvgl-widget', 'widget', 'component', 'element'].includes(s.type)) ||
                                    // OR... it's a child element of it's parent (like lv_dropdown-list under lv_dropdown)
                                    (s.type.indexOf('child') !== -1 && s.name.startsWith(elementName))
                            )
                        )
                        .map(([name]) => name)
                );
            }

            // Walk all ancestors and combine their allowed children, deduplicating.
            const combined = new Set<string>();

            // Add element-specific child elements (e.g. lv_dropdown-list for lv_dropdown)
            for (const [name, s] of this.elements) {
                if (s.type.includes('child') && s.name.startsWith(elementName)) {
                    combined.add(name);
                }
            }

            // NOTE: this will also include lv_obj due to recursivity
            this.walkInheritanceChain(schema.extends, (_, ancestorName) => {
                for (const child of this.getAllowedChildrenForElement(ancestorName)) {
                    combined.add(child);
                }
            });

            return [...combined].sort();
        }
        return schema.allowedChildren;
    }

    /** Expand `<api><children allow="..."/></api>` tokens to concrete element names. */
    resolveApiChildrenAllow(ownerName: string, tokens: string[]): string[] {
        const resolved: Set<string> = new Set();

        for (const token of tokens) {
            if (token === 'events') {
                for (const [name, elementSchema] of this.elements.entries()) {
                    if (!name.includes('event')) continue;
                    if (elementSchema.type !== 'lvgl-element-child' && !name.startsWith('lv_obj-')) continue;
                    if (!name.startsWith('lv_obj-') && elementSchema.parent !== 'lv_obj') continue;

                    resolved.add(name);
                    if (name.startsWith('lv_obj-')) resolved.add(name.replace(/^lv_obj-/, ''));
                }
                continue;
            }

            const prefixed: string = `${ownerName}-${token}`;
            const candidates: string[] = [prefixed, token, `lv_obj-${token}`];

            for (const candidate of candidates) {
                if (!this.hasElement(candidate)) continue;
                resolved.add(candidate);
                if (candidate.startsWith('lv_obj-')) resolved.add(candidate.replace(/^lv_obj-/, ''));
            }
        }

        return Array.from(resolved).sort();
    }

    /** Whether this element may contain nested child elements in XML (container vs self-closing completion). */
    hasAllowedChildren(elementName: string): boolean {
        const schema: ElementSchema | undefined = this.getElement(elementName);
        if (!schema) return false;
        if (schema?.extends?.length) {
            const extendedSchema: ElementSchema | undefined = this.getElement(schema.extends);
            if (extendedSchema?.allowedChildren?.length || (extendedSchema && !extendedSchema.allowedChildren))
                return true;
            else return false;
        }
        if (schema?.allowedChildren?.length) return true;

        return false;
    }

    // Try direct name and explicit lv_obj- prefix
    hasElement(elementName: string): boolean {
        return this.elements.has(elementName) || this.elements.has(`lv_obj-${elementName}`);
    }

    // In the case of multiple elements with the same name,
    // the `parentType` can be critical to determine which element to return.
    getElement(elementName: string, parentType?: string): ElementSchema | undefined {
        // TODO: this rule is a hack, containing too much implicit logic of the schema [Architectural Improvement]
        // The way this should be done properly (not hardcoding 'styles'), would be:
        // ultimateAncestorOf(parentType) ==== 'lv_obj' ? 'lv_obj' : parentType;
        //
        // The reason for this logic is <styles> and lv_obj types both have a
        // 'style' property. However, it should be handled correctly.
        parentType = parentType === 'styles' ? 'styles' : 'lv_obj';

        // Advanced search:
        // If `parentType` is provided, give precedence to any element
        // which has that parent. Do not require it, since parent is optional.
        // TODO: We need to search to this advanced search only for every element
        // which has a duplicate. How do we know that? Duplicates are currently only
        // possible when `lv_obj-something` and `something` both exist. So we could look
        // up both, and go from there. [Architectural Improvement]

        // Try finding the element as given
        const asGivenElement: ElementSchema | undefined = this.elements.get(elementName);
        // If the element is already prefixed, this will be double-prefixed and therefore not found
        const objPrefixedElement: ElementSchema | undefined = this.elements.get(`lv_obj-${elementName}`);

        if (parentType) {
            // If one of the options has a `parent` matching `parentType`, it will return that.
            // Otherwise, it will just continue.
            const element: ElementSchema | undefined = [asGivenElement, objPrefixedElement]
                .filter((el) => el !== undefined)
                .find((el) => el.parent === parentType);

            if (element) return element;
        }

        // Try element as given (could be shorthand) and then explicit lv_obj- prefix.
        return asGivenElement || objPrefixedElement;
    }

    private walkInheritanceChain(
        startElementName: string | undefined,
        cb: (schema: ElementSchema, elementName: string) => boolean | void
    ): void {
        if (!startElementName) return;

        const schema: ElementSchema | undefined = this.getElement(startElementName);
        if (!schema) return;

        if (cb(schema, startElementName) === true) return;
        if (schema.extends) this.walkInheritanceChain(schema.extends, cb);
    }

    // TODO: Create common function to combine the properties and args of an element. [Architectural Improvement]
    // TODO: Need to alter this to follow component and widget inheritance rules. [Architectural Improvement]
    // Recursively looks up a property, starting with the immediate element and searching up the inheritance chain.
    // Returns the object type (custom component name, `lv_image`, `lv_obj`, etc.), if found.
    lookupProperty(
        propName: string,
        elementName: string
    ): { objectType: string; propSchema: PropertySchema; elementSchema: ElementSchema } | null {
        const elementSchema: ElementSchema | undefined = this.getElement(elementName, 'lv_obj');

        if (elementSchema !== undefined) {
            let match: { objectType: string; propSchema: PropertySchema; elementSchema: ElementSchema } | null = null;

            this.walkInheritanceChain(elementName, (schemaInChain: ElementSchema, inheritedElementName: string) => {
                const propSchema: PropertySchema | undefined = (schemaInChain.properties || [])
                    .concat(schemaInChain.args || [])
                    .find((p) => p.name === propName);

                if (!propSchema) return false;

                match = {
                    objectType:
                        // Prefix the element name with lv_obj if it resolves to an lv_obj-* schema.
                        !inheritedElementName.startsWith('lv_obj') && schemaInChain.name.startsWith('lv_obj')
                            ? `lv_obj-${inheritedElementName}`
                            : inheritedElementName,
                    propSchema,
                    elementSchema: schemaInChain
                };
                return true;
            });

            if (match) return match;
        } else {
            // Definition not found, cannot proceed.
        }

        return null;
    }

    // Check if a given property has a default value
    // Used in CompletionProvider to check if the property has a default value
    hasDefaultProperty(elementName: string, propName: string): boolean {
        const schema: ElementSchema | undefined = this.getElement(elementName);
        const prop: PropertySchema | undefined = schema?.properties?.find((p) => p.name === propName);
        return Boolean(prop?.default);
    }

    // Get the default value of a given property
    // Used in CompletionProvider to get the default value of a property
    getDefaultProperty(elementName: string, propName: string): string {
        const schema: ElementSchema | undefined = this.getElement(elementName);
        const prop: PropertySchema | undefined = schema?.properties?.find((p) => p.name === propName);
        return prop?.default || '';
    }

    // Only works if you give it `style_something` - doesn't work for `something`
    public isGlobalStyleProp(styleName: string): boolean {
        return styleName.startsWith('style_') && this.globalStyleProps.has(styleName.slice(6));
    }

    private clearAllDataStructures(): void {
        this.elements.clear();
        this.enums.clear();
        this.globalStyleProps.clear();
        this.globalStyles.clear();
        this.globalApiProps.clear();
        this.globalConsts.clear();
        this.consts.clear();
        this.colors = [];
        this.fonts = [];
        this.images = [];
        this.projectTargets = [];
        this.projectFolders = [];
        this.subjectsByType = {};
        this.subjectsByFile.clear();
        this.translationTagsByFile.clear();
        this.screenNames.clear();
        clearKnownProjectValidationState();
    }

    // Dispose of the schema service
    dispose(): void {
        // Clear all data structures
        this.clearAllDataStructures();
        this.hotReloadListeners.clear();

        // Remove the singleton instance
        LVGLSchemaService.instance = null;
    }

    getConstDefinition(name: string): ConstDefinitionData | undefined {
        return this.consts.get(name);
    }

    private registerConstsFromDocument(document: Document, filePath: string, isGlobal: boolean): void {
        document.querySelectorAll('consts > *').forEach((constant: Element) => {
            const name: string | null = constant.getAttribute('name');
            if (!name) throw new Error('Constant does not have a name');

            const constDef: ConstDefinitionData = {
                type: constant.tagName,
                name,
                value: constant.getAttribute('value') || '',
                help: constant.getAttribute('help') || '',
                filePath
            };

            this.consts.set(name, constDef);
            if (isGlobal) this.globalConsts.set(name, constDef);
        });
    }

    private clearConstsForFile(filePath: string): void {
        if (!filePath) return;
        this.consts.forEach((def: ConstDefinitionData, name: string) => {
            if (def.filePath === filePath) this.consts.delete(name);
        });
        this.globalConsts.forEach((def: ConstDefinitionData, name: string) => {
            if (def.filePath === filePath) this.globalConsts.delete(name);
        });
    }
}
